import { listPaddocksByOrganizationQueryOptions } from "@packages/api-client/hooks/paddockController";
import type {
	CreatePaddockMutationRequest,
	UpdatePaddockMutationRequest,
} from "@packages/api-client/types/paddockController";
import type { GeoJSONPolygon } from "@packages/shared-types/grass";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useCreatePaddock } from "@/api/grass";
import { PageHeader } from "@/components/PageHeader";
import { requireReachableRoute } from "@/lib/offline/routeGuard";
import { PaddockForm } from "./-components/PaddockForm";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/grass-production/farms/$farmId/paddocks/new",
)({
	// Paddock setup (boundary drawing on the map) is desk work — online-only.
	staticData: { offline: "online-only" },
	beforeLoad: () => requireReachableRoute("online-only"),
	loader: ({ context, params }) => {
		// Prefetch the farm's paddocks: they render as map context and their
		// bounds frame the map on the farm instead of the fallback centre.
		context.queryClient.ensureQueryData(
			listPaddocksByOrganizationQueryOptions(params.orgId, {
				establishmentId: params.farmId,
			}),
		);
	},
	component: NewPaddockPage,
});

function NewPaddockPage() {
	const { orgId, farmId } = Route.useParams();
	const { t } = useTranslation();
	const navigate = Route.useNavigate();

	const createMutation = useCreatePaddock();

	const { data: paddocksResponse } = useQuery(
		listPaddocksByOrganizationQueryOptions(orgId, { establishmentId: farmId }),
	);

	const contextPaddocks = useMemo(
		() =>
			(paddocksResponse?.data ?? []).flatMap((p) =>
				p.boundaryGeoJson
					? [
							{
								id: p.id,
								name: p.name,
								boundary: p.boundaryGeoJson as GeoJSONPolygon,
							},
						]
					: [],
			),
		[paddocksResponse],
	);

	const handleSubmit = (
		formData: CreatePaddockMutationRequest | UpdatePaddockMutationRequest,
	) =>
		createMutation
			.mutateAsync({
				organizationId: orgId,
				establishmentId: farmId,
				data: formData as CreatePaddockMutationRequest,
			})
			.then(() => {
				navigate({
					to: "/app/org/$orgId/grass-production/farms/$farmId/paddocks",
					params: { orgId, farmId },
				});
			});

	return (
		// Containment is lg-only: on mobile the form's own panels sit on the page
		// background and the page scrolls (the card frame is lg-only too — a card
		// around panels would double the gutters on a phone); on desktop the card
		// fills the viewport and the form column scrolls inside it
		// (overflow-hidden stops content spilling past the card). `-mt-3` cancels
		// `<main>`'s `p-3` top gutter on phones, as the herd create page does.
		<div className="-mt-3 flex flex-col gap-2 md:mt-0 md:gap-3 lg:h-full">
			<PageHeader title={t("grass.paddock.create.title")} showBackButton />
			<div className="lg:card lg:flex-1 lg:min-h-0 lg:overflow-hidden lg:bg-base-100 lg:shadow-md">
				<div className="lg:card-body lg:h-full lg:min-h-0 lg:p-4">
					<PaddockForm
						onSubmit={handleSubmit}
						isSubmitting={createMutation.isPending}
						onCancel={() =>
							navigate({
								to: "/app/org/$orgId/grass-production/farms/$farmId/paddocks",
								params: { orgId, farmId },
							})
						}
						layout="side-by-side"
						contextPaddocks={contextPaddocks}
					/>
				</div>
			</div>
		</div>
	);
}
